import './caseStudy.css'
import Footer from "./footer"
import RevealTitle from "./RevealTitle"

const PIPELINE = ["Upload", "Read frames", "CLAHE enhance", "YOLO detect", "Blur", "Export"];

const CHALLENGES = [
    "Faces are hard to spot in low light and night scenes.",
    "People appear small, distant, or partly turned away.",
    "Crowded scenes mean many faces per frame.",
    "Bright lights confuse simpler detectors.",
];

const SOLUTIONS = [
    "A YOLO deep-learning detector, not a classical cascade.",
    "CLAHE lifts contrast before every detection pass.",
    "A low confidence threshold catches partial and angled faces.",
    "Holds up across many real-world scenes.",
];

const FEATURES = [
    { h: "Any lighting", b: "Detects faces in day, night, and low-contrast scenes." },
    { h: "Full automation", b: "Upload once — every frame is processed end to end." },
    { h: "Live progress", b: "A real-time bar tracks frame-by-frame processing." },
    { h: "Detection stats", b: "Reports frames processed and total faces blurred." },
    { h: "Strong blur", b: "Face-scaled blur keeps identities hidden." },
    { h: "One-click export", b: "Download the finished, privacy-safe video instantly." },
];

const STACK = [
    { title: "Core", items: ["Python", "NumPy"] },
    { title: "Vision", items: ["OpenCV", "YOLOv10n-face", "Ultralytics", "CLAHE"] },
    { title: "Interface", items: ["Streamlit"] },
];

export default function Fblur() {
    return (
        <>
            <div className="cs-wrap">
                <header className="cs-hero">
                    <p className="cs-eyebrow">Case study · AI · Etisalat</p>
                    <RevealTitle text="FBlur" className="cs-title" />
                    <p className="cs-subtitle">
                        Automated privacy protection — detecting and blurring every face in a video with a deep-learning pipeline.
                    </p>
                </header>

                <div className="cs-links">
                    <a className="cs-linkBtn" href="https://github.com/mohammed0Swalih/fblur" target="_blank" rel="noreferrer">GitHub ↗</a>
                </div>

                <div className="cs-cover">
                    <div className="cs-ph cs-ph--cover">Streamlit UI / before → after frame — screenshot (coming)</div>
                </div>

                <div className="cs-meta">
                    <div><p className="cs-meta-label">Role</p><p className="cs-meta-value">Computer vision (solo)</p></div>
                    <div><p className="cs-meta-label">Context</p><p className="cs-meta-value">Etisalat internship</p></div>
                    <div><p className="cs-meta-label">Type</p><p className="cs-meta-value">Video privacy pipeline</p></div>
                    <div><p className="cs-meta-label">Interface</p><p className="cs-meta-value">Streamlit</p></div>
                </div>

                {/* Overview */}
                <section className="cs-section">
                    <h2 className="cs-label">Overview</h2>
                    <p className="cs-statement">Privacy by design — no face leaves the frame unblurred.</p>
                    <p className="cs-body">
                        The tool sets out to do three things: <strong>detect</strong> every human face across all frames of a video in any
                        lighting, <strong>blur</strong> each one strongly enough to protect identity, and <strong>deliver</strong> it through a
                        simple UI where you upload a clip, watch it process, and download the privacy-safe result.
                    </p>
                </section>

                {/* Pipeline */}
                <section className="cs-section">
                    <h2 className="cs-label">The pipeline</h2>
                    <p className="cs-statement">From an uploaded clip to a blurred export.</p>
                    <div className="cs-flow">
                        {PIPELINE.map((s, i, a) => (
                            <span className="cs-flow-item" key={s}>{s}{i < a.length - 1 && <span className="cs-flow-arrow">→</span>}</span>
                        ))}
                    </div>
                    <p className="cs-body">
                        OpenCV loops the video frame by frame. Before each detection pass, <code>CLAHE</code> lifts contrast so faces
                        surface in dark scenes. YOLO returns bounding boxes, each face region is blurred in place, and the frames are
                        written back out to a new MP4 — original resolution and frame rate preserved.
                    </p>
                </section>

                {/* Challenge & solution */}
                <section className="cs-section">
                    <h2 className="cs-label">Challenge &amp; solution</h2>
                    <p className="cs-statement">Making detection reliable in the real world.</p>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">The challenge</h3>
                            <ul className="cs-list">{CHALLENGES.map((c) => <li key={c}>{c}</li>)}</ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">The solution</h3>
                            <ul className="cs-list">{SOLUTIONS.map((s) => <li key={s}>{s}</li>)}</ul>
                        </div>
                    </div>
                </section>

                {/* Under the hood */}
                <section className="cs-section">
                    <h2 className="cs-label">Under the hood</h2>
                    <p className="cs-statement">The decisions that made it fast enough to be usable.</p>

                    <div className="cs-item">
                        <p className="cs-item-head">A cheap blur that looks expensive</p>
                        <p className="cs-item-body">
                            Instead of running a large Gaussian kernel over every face, each region is shrunk to <code>1/12</code> of its
                            size and scaled straight back up. It reads as just as blurred, but costs a fraction of the compute — and the
                            blur automatically scales with the face.
                        </p>
                    </div>

                    <div className="cs-item">
                        <p className="cs-item-head">Detect every 3rd frame, not every frame</p>
                        <p className="cs-item-body">
                            YOLO inference is the expensive step, so it only runs on every third frame; the boxes are reused in between.
                            To stop faces drifting out of a reused box, every box is padded by <code>12%</code> — cutting inference cost
                            by ~3× with no visible leakage.
                        </p>
                    </div>

                    <div className="cs-item">
                        <p className="cs-item-head">Tuned for recall, not precision</p>
                        <p className="cs-item-body">
                            Detection runs at <code>conf = 0.3</code> and <code>iou = 0.45</code> at <code>imgsz = 480</code>. A deliberately
                            low threshold means partial and angled faces still get caught — for a privacy tool, a false positive costs
                            nothing, but a missed face costs everything.
                        </p>
                    </div>

                    <div className="cs-item">
                        <p className="cs-item-head">Contrast where it counts</p>
                        <p className="cs-item-body">
                            CLAHE is applied to the <code>L</code> (lightness) channel in LAB colour space — <code>clipLimit 3.0</code>,
                            8×8 tiles — so low-light detail lifts without wrecking the colours of the frame that gets written out.
                        </p>
                    </div>
                </section>

                {/* Features */}
                <section className="cs-section">
                    <h2 className="cs-label">Key features</h2>
                    <div className="cs-featureGrid">
                        {FEATURES.map((f) => (
                            <div className="cs-feature" key={f.h}>
                                <h4 className="cs-item-head">{f.h}</h4>
                                <p className="cs-item-body">{f.b}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Stack */}
                <section className="cs-section">
                    <h2 className="cs-label">Tech stack</h2>
                    <div className="cs-stackGroups">
                        {STACK.map((g) => (
                            <div className="cs-stackGroup" key={g.title}>
                                <h4 className="cs-stackTitle">{g.title}</h4>
                                <div className="cs-chips">{g.items.map((it) => <span className="cs-chip" key={it}>{it}</span>)}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Delivered + roadmap */}
                <section className="cs-section">
                    <h2 className="cs-label">What I built &amp; what's next</h2>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">Delivered</h3>
                            <ul className="cs-list">
                                <li>An end-to-end video face-blurring pipeline (OpenCV + YOLOv10n-face).</li>
                                <li>CLAHE pre-processing for low-light and night footage.</li>
                                <li>Frame-skipping detection with padded box reuse for speed.</li>
                                <li>A Streamlit UI with upload, live progress, stats, and export.</li>
                                <li>Stats on frames processed, faces blurred, fps, and resolution.</li>
                            </ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">Roadmap</h3>
                            <ul className="cs-list">
                                <li>Face tracking between detections instead of static box reuse.</li>
                                <li>GPU batch inference for full-length footage.</li>
                                <li>A real-time webcam mode.</li>
                                <li>Selective blur — keep consented faces sharp.</li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </>
    )
}
